import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Caption from 'components/Caption';
import LazyLoad from 'components/LazyLoad';
import Picture from 'components/Picture';

import * as CustomPropTypes from 'lib/CustomPropTypes';
import buildAffiliateLink from 'lib/buildAffiliateLink';
import { imageAltForImage } from 'lib/imageAlt';
import AIMS_FLAVORS from 'lib/aimsFlavors';

import styles from './styles.module.scss';

const sizes = {
  portrait: {
    small: {
      s: AIMS_FLAVORS.FIT_760w,
      m: AIMS_FLAVORS.FIT_560w,
      l: AIMS_FLAVORS.FIT_320w,
      xl: AIMS_FLAVORS.FIT_360w,
    },
    medium: {
      s: AIMS_FLAVORS.FIT_760w,
      l: AIMS_FLAVORS.FIT_560w,
    },
    large: {
      s: AIMS_FLAVORS.FIT_760w,
      m: AIMS_FLAVORS.FIT_560w,
      l: AIMS_FLAVORS.FIT_320w,
      xl: AIMS_FLAVORS.FIT_678w,
    },
  },
  landscape: {
    small: {
      s: AIMS_FLAVORS.FIT_760w,
      m: AIMS_FLAVORS.FIT_560w,
      l: AIMS_FLAVORS.FIT_320w,
      xl: AIMS_FLAVORS.FIT_360w,
    },
    medium: {
      s: AIMS_FLAVORS.FIT_760w,
      l: AIMS_FLAVORS.FIT_560w,
    },
    large: {
      s: AIMS_FLAVORS.FIT_760w,
      m: AIMS_FLAVORS.FIT_1000w,
      xl: AIMS_FLAVORS.FIT_860w,
    },
  },
};

function isPortrait(image) {
  return image.height > image.width;
}

function isSmall(presentation) {
  return presentation === 'small' || presentation === 'halfsize';
}

function isMedium(presentation) {
  return presentation === 'medium' || presentation === 'normal' || presentation === null;
}

function isLarge(presentation) {
  return !isSmall(presentation) && !isMedium(presentation);
}

function classes(image, presentation, ecommerceEnabled) {
  if (ecommerceEnabled) {
    return classNames(styles.ecom, styles.container, styles.medium);
  }

  return classNames(styles.container, {
    [styles.portrait]: isPortrait(image),
    [styles.small]: isSmall(presentation),
    [styles.medium]: isMedium(presentation),
    [styles.large]: isLarge(presentation),
  });
}

function getHeight(ratio, orientation, presentation, ecommerceEnabled) {
  let dimensions = sizes[orientation];

  if (isSmall(presentation)) {
    dimensions = dimensions.small;
  } else if (ecommerceEnabled || isMedium(presentation)) {
    dimensions = dimensions.medium;
  } else {
    dimensions = dimensions.large;
  }

  return dimensions;
}

function getImgRatio(image, orientation, presentation, ecommerceEnabled) {
  const ratio = image.height / image.width;
  return getHeight(ratio, orientation, presentation, ecommerceEnabled);
}

function pictureSizes(image, presentation, ecommerceEnabled) {
  const orientation = isPortrait(image) ? 'portrait' : 'landscape';
  return getImgRatio(image, orientation, presentation, ecommerceEnabled);
}


class InlineImage extends React.Component {
  static propTypes = {
    content: CustomPropTypes.image.isRequired,
    presentation: PropTypes.string.isRequired,
    ecommerceEnabled: PropTypes.bool,
  };

  static defaultProps = {
    ecommerceEnabled: false,
  };

  componentDidMount() {
    if (!this.shopLink) {
      return;
    }

    Array.prototype.forEach.call(this.shopLink.querySelectorAll('a'), (el) => {
      const href = buildAffiliateLink(el.href);
      el.setAttribute('href', href);
      el.setAttribute('target', '_blank');
    });
  }

  render() {
    const {
      content: { image, shopLink },
      presentation,
      ecommerceEnabled,
    } = this.props;
    const { height, width } = image;
    return (
      <figure className={classes(image, presentation, ecommerceEnabled)}>
        {/* TODO: [BENTO-11161] Temporarily disable placeholders on InlineImage */}
        <LazyLoad placeholder={false}>
          <Picture
            sizes={pictureSizes(image, presentation, ecommerceEnabled)}
            responsiveFlavors={pictureSizes(image, presentation, ecommerceEnabled)}
            url={image.url.primary}
            className={classNames(styles.theimg, {
              [styles.nocaption]:
                image.caption
                && image.caption.length <= 0
                && isPortrait(image)
                && isSmall(presentation),
            })}
            flavor="fit"
            alt={imageAltForImage(image)}
            originalHeight={height}
            originalWidth={width}
            retina
          />
        </LazyLoad>
        <Caption
          image={image}
          className={classNames(styles.caption, 'mt4')}
        />
        {ecommerceEnabled && (
          <p
            ref={(theShopLink) => {
              this.shopLink = theShopLink;
            }}
            className={classNames(styles.shopLink, 'icon', 'icon-shop')}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: shopLink.html }}
          />
        )}
      </figure>
    );
  }
}

export default InlineImage;
